import { useMemo } from 'react';

import { Trans } from '@lingui/react/macro';
import { Button } from '@repo/videa-components/src/ui/button';

import { SelectableOption } from '@/common-components/elements/SelectableOption/SelectableOption';

import type { FeedbackOption } from '../types';

interface FeedbackViewProps {
  rating: 'up' | 'down' | null;
  upOptions: FeedbackOption[];
  downOptions: FeedbackOption[];
  selectedFeedbackOptions: string[];
  additionalComments: string;
  onToggleOption: (value: string) => void;
  onCommentsChange: (comments: string) => void;
  onSubmit: () => void;
  onCancel?: () => void;
}

export const FeedbackView = ({
  rating,
  upOptions,
  downOptions,
  selectedFeedbackOptions,
  additionalComments,
  onToggleOption,
  onCommentsChange,
  onSubmit,
  onCancel,
}: FeedbackViewProps) => {
  // state
  const currentOptions = useMemo(() => {
    return rating === 'up' ? upOptions : downOptions;
  }, [rating, upOptions, downOptions]);

  // variables
  const feedbackOptions = useMemo(
    () => (
      <div className="vai-flex vai-flex-wrap vai-gap-3">
        {currentOptions.map((option) => (
          <SelectableOption
            key={option.value}
            value={option.value}
            isSelected={selectedFeedbackOptions.includes(option.value)}
            onToggle={onToggleOption}
          >
            {option.label}
          </SelectableOption>
        ))}
      </div>
    ),
    [currentOptions, selectedFeedbackOptions, onToggleOption],
  );

  return (
    <div data-testid="feedback-view">
      <div className="vai-space-y-4">
        {/* Feedback Options */}
        <div className="vai-space-y-3">{feedbackOptions}</div>
        {/* Additional Comments - Always shown */}
        <div className="vai-space-y-2">
          <label
            htmlFor="feedback-comments"
            className="vai-text-sm vai-font-medium vai-text-gray-700"
          >
            <Trans>Share your feedback with the team</Trans>
          </label>
          <textarea
            id="feedback-comments"
            value={additionalComments}
            onChange={(e) => onCommentsChange(e.target.value)}
            className="vai-w-full vai-h-24 vai-p-3 vai-border vai-border-gray-300 vai-rounded-md vai-resize-none vai-focus:vai-outline-none vai-focus:vai-ring-2 vai-focus:vai-ring-blue-500 vai-focus:vai-border-transparent"
            placeholder="Type feedback here..."
            data-testid="feedback-comments"
          />
        </div>
      </div>
      <div className="vai-flex vai-justify-end vai-gap-2 vai-pt-0">
        {/* Cancel button */}
        {onCancel && (
          <Button
            variant="outline"
            onClick={onCancel}
            data-testid="dialog-feedback-cancel-button"
          >
            <Trans>Cancel</Trans>
          </Button>
        )}
        {/* Submit feedback button */}
        <Button onClick={onSubmit} data-testid="dialog-feedback-submit-button">
          <Trans>Submit feedback</Trans>
        </Button>
      </div>
    </div>
  );
};
